import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Select, Button, Space, Typography, List, Tag, Radio, Spin, Alert, message, Empty } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { appointmentApi, AvailableTimeSlot } from '../../services/appointmentApi';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface AvailabilityPickerProps {
  appointmentType: 'diagnosis' | 'repair' | 'inspection';
  customerId?: number;
  vehicleId?: number;
  estimatedDuration?: number;
  locationAddress?: string;
  onSelectSlot: (slot: AvailableTimeSlot) => void;
  selectedSlot?: AvailableTimeSlot | null;
}

export const AvailabilityPicker: React.FC<AvailabilityPickerProps> = ({
  appointmentType,
  customerId,
  vehicleId,
  estimatedDuration,
  locationAddress,
  onSelectSlot,
  selectedSlot
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs().add(1, 'day'));
  const [duration, setDuration] = useState<number>(estimatedDuration || getDefaultDuration(appointmentType));
  const [availableSlots, setAvailableSlots] = useState<AvailableTimeSlot[]>([]);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<number | 'any'>('any');
  const [viewMode, setViewMode] = useState<'recommended' | 'all'>('recommended');

  useEffect(() => {
    if (selectedDate) {
      loadAvailability();
    }
  }, [selectedDate, duration, appointmentType]);

  useEffect(() => {
    if (estimatedDuration && estimatedDuration !== duration) {
      setDuration(estimatedDuration);
    }
  }, [estimatedDuration]);

  const loadAvailability = async () => {
    setLoading(true);
    try {
      const startDate = selectedDate.startOf('day').toISOString();
      const endDate = selectedDate.endOf('day').toISOString();

      const slots = await appointmentApi.checkAvailability({
        start_date: startDate,
        end_date: endDate,
        duration_minutes: duration,
        appointment_type: appointmentType,
        customer_id: customerId,
        location_address: locationAddress
      });

      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error loading availability:', error);
      message.error('Failed to load available time slots');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredSlots = () => {
    let filtered = availableSlots.filter(slot => slot.available);

    // Filter by technician if selected
    if (selectedTechnicianId !== 'any') {
      filtered = filtered.filter(slot => slot.technician_id === selectedTechnicianId);
    }

    // Filter by view mode
    if (viewMode === 'recommended') {
      filtered = filtered.filter(slot => slot.recommended);
    }

    // Sort by time
    return filtered.sort((a, b) =>
      dayjs(a.start_time).unix() - dayjs(b.start_time).unix()
    );
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      // Don't allow booking in the past
      if (date.isBefore(dayjs(), 'day')) {
        message.warning('Cannot book appointments in the past');
        return;
      }
      setSelectedDate(date);
    }
  };

  const handleSelectSlot = (slot: AvailableTimeSlot) => {
    onSelectSlot(slot);
  };

  const disabledDate = (current: Dayjs) => {
    // Can't select dates before today
    return current && current.isBefore(dayjs(), 'day');
  };

  const getUniqueTechnicians = () => {
    const techMap = new Map<number, string>();
    availableSlots.forEach(slot => {
      if (!techMap.has(slot.technician_id)) {
        techMap.set(slot.technician_id, slot.technician_name);
      }
    });
    return Array.from(techMap.entries()).map(([id, name]) => ({ id, name }));
  };

  const filteredSlots = getFilteredSlots();
  const technicians = getUniqueTechnicians();

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Select Appointment Date & Time</Title>
          <Text type="secondary">
            Choose an available time slot for your {appointmentType} appointment
          </Text>
        </div>

        {/* Controls */}
        <Space wrap>
          <div>
            <Text strong>Date:</Text>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              disabledDate={disabledDate}
              style={{ marginLeft: 8, width: 200 }}
              format="MMM DD, YYYY"
            />
          </div>

          <div>
            <Text strong>Duration:</Text>
            <Select
              value={duration}
              onChange={setDuration}
              style={{ marginLeft: 8, width: 150 }}
            >
              <Option value={15}>15 minutes</Option>
              <Option value={30}>30 minutes</Option>
              <Option value={45}>45 minutes</Option>
              <Option value={60}>1 hour</Option>
              <Option value={90}>1.5 hours</Option>
              <Option value={120}>2 hours</Option>
              <Option value={180}>3 hours</Option>
              <Option value={240}>4 hours</Option>
            </Select>
          </div>

          {technicians.length > 1 && (
            <div>
              <Text strong>Technician:</Text>
              <Select
                value={selectedTechnicianId}
                onChange={setSelectedTechnicianId}
                style={{ marginLeft: 8, width: 180 }}
              >
                <Option value="any">Any Available</Option>
                {technicians.map(tech => (
                  <Option key={tech.id} value={tech.id}>{tech.name}</Option>
                ))}
              </Select>
            </div>
          )}
        </Space>

        <Radio.Group
          value={viewMode}
          onChange={(e) => setViewMode(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="recommended">
            <CheckCircleOutlined /> Recommended Times
          </Radio.Button>
          <Radio.Button value="all">
            <CalendarOutlined /> All Available
          </Radio.Button>
        </Radio.Group>

        {/* Selected Slot Display */}
        {selectedSlot && (
          <Alert
            type="success"
            message="Selected Time Slot"
            description={
              <div>
                <Text strong>{dayjs(selectedSlot.start_time).format('MMM DD, YYYY')}</Text>
                <br />
                <Text>{dayjs(selectedSlot.start_time).format('h:mm A')} - {dayjs(selectedSlot.end_time).format('h:mm A')}</Text>
                <br />
                <Text type="secondary">Technician: {selectedSlot.technician_name}</Text>
              </div>
            }
            showIcon
          />
        )}

        {/* Available Slots List */}
        <Spin spinning={loading}>
          {filteredSlots.length === 0 ? (
            <Empty
              description={
                viewMode === 'recommended'
                  ? "No recommended slots available. Try 'All Available' or select a different date."
                  : "No available time slots for this date. Please select another date."
              }
            />
          ) : (
            <List
              dataSource={filteredSlots}
              renderItem={(slot) => {
                const isSelected = selectedSlot
                  && selectedSlot.start_time === slot.start_time
                  && selectedSlot.technician_id === slot.technician_id;

                return (
                  <List.Item
                    style={{
                      cursor: 'pointer',
                      padding: '16px',
                      border: isSelected ? '2px solid #1890ff' : '1px solid #f0f0f0',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      backgroundColor: isSelected ? '#e6f7ff' : '#fff'
                    }}
                    onClick={() => handleSelectSlot(slot)}
                  >
                    <List.Item.Meta
                      avatar={
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          backgroundColor: '#1890ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff'
                        }}>
                          <ClockCircleOutlined style={{ fontSize: 24 }} />
                        </div>
                      }
                      title={
                        <Space>
                          <Text strong style={{ fontSize: 16 }}>
                            {dayjs(slot.start_time).format('h:mm A')} - {dayjs(slot.end_time).format('h:mm A')}
                          </Text>
                          {slot.recommended && (
                            <Tag color="green">RECOMMENDED</Tag>
                          )}
                          {isSelected && (
                            <Tag color="blue">SELECTED</Tag>
                          )}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small">
                          <Space>
                            <UserOutlined />
                            <Text>{slot.technician_name}</Text>
                          </Space>
                          {slot.travel_time_minutes && slot.travel_time_minutes > 0 && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Includes {slot.travel_time_minutes} min travel time
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          )}
        </Spin>

        {/* Helper Text */}
        <Alert
          type="info"
          message="Booking Tips"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Recommended times are typically mid-morning to early afternoon</li>
              <li>Earlier appointments may have shorter wait times</li>
              <li>Select "Any Available" technician for more time slot options</li>
              <li>Duration can be adjusted if you need more or less time</li>
            </ul>
          }
        />
      </Space>
    </Card>
  );
};

// Helper function to get default duration based on appointment type
function getDefaultDuration(type: 'diagnosis' | 'repair' | 'inspection'): number {
  switch (type) {
    case 'diagnosis':
      return 60;
    case 'repair':
      return 120;
    case 'inspection':
      return 30;
    default:
      return 60;
  }
}
